import React, { useState, useEffect } from 'react'
import _isEmpty from 'lodash/isEmpty'
import styled from 'styled-components'
import SC from '../Vouchers/Account/elements'
import useVoucherCodeHook from '../Vouchers/Account/useVoucherTabHook'
import VoucherDetailsPopUp from '../Vouchers/Account/VoucherDetailsPopUp'
import { AccountConsumer } from '../../components/AccountProvider'
import { EventTrackingConsumer } from '../EventTrackingProvider/EventTrackingProvider'
import VirtualCarousel from '../VirtualCarousel/VirtualCarousel'
import SideSheet from '@ntuctech/devex-tangram/SideSheet/SideSheet'
import { SCREEN_SIZE } from '../../lib/Media'
import VoucherCard from '../VoucherSwimlane/VoucherCard'
import coupon from '../icons/svg/coupon.svg'
import { GTM_ENV_VAR as GEV } from '../../lib/eventTrackerFunction'
import { AMP_EVENT_NAME, getPageTypeByUrl } from '../../lib/amplitude'
import { getCtaLocationMeta } from '../../lib/utils'

const VoucherSwimLaneContainer = styled.div`
  margin-bottom: 1rem;
`

const SwimlaneTitle = styled.h2`
  font-weight: 700;
  color: #333333;
  font-size: 1rem;
  line-height: 1.5rem;
  ${SCREEN_SIZE.Below.Tablet} {
    font-size: 1rem;
  }
`

const SwimlaneDesc = styled.h2`
  font-weight: 400;
  color: #333333;
  font-size: 0.875rem;
  line-height: 1.25rem;
  ${SCREEN_SIZE.Below.Tablet} {
    font-size: 1rem;
  }
`

const couponImage = () => `url(${coupon})`

const CouponSpace = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  background-size: contain;
  background-repeat: no-repeat;
  background-image: ${couponImage};
  margin-right: 0.5rem;
`

const TitleBlock = styled.div`
  display: flex;
  margin-bottom: 0.25rem;
`
const DescBlock = styled.div`
  display: flex;
  margin-bottom: 0.75rem;
`

const PdpVoucherSwimlaneContainer = ({
  track,
  productId = '',
  storeId = '',
  categoryIds = {},
  voucherTreatment = '',
  isThreeColumnLayout = false,
}) => {
  const [state, dispatch] = useVoucherCodeHook({
    productId,
    storeId,
    categoryIds,
  })
  const [voucherData, setVoucherData] = useState([])
  const [openSideSheet, setOpenSideSheet] = useState(false)
  const activeDataList = state.activeData?.list
  const isEmpty = voucherData?.length === 0

  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => {
    setIsMounted(true)
  }, [])

  const trackVouchers = data => {
    const getAllOfferIds = data.map(item => item.id).join()
    track({
      [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.VOUCHER_SWIMLANE_VIEWED,
      [GEV.AMPLITUDE_CTA_LOCATION]: {
        pageName: getPageTypeByUrl(),
        pageMeta: getCtaLocationMeta() || [],
      },
      [GEV.AMPLITUDE_VERTICAL]: 'grocery-online',
      [GEV.AMPLITUDE_VOUCHER_OFFER_ID]: getAllOfferIds,
      [GEV.AMPLITUDE_SALES_ORDER_ID]: null,
      [GEV.AMPLITUDE_IS_VOUCHER_AVAILABLE]: data?.length > 0 ? 'yes' : 'no',
      [GEV.AMPLITUDE_IS_VOUCHER_SHOWN]:
        voucherTreatment === 'B' && data?.length ? 'yes' : 'no',
    })
  }

  useEffect(() => {
    if (state?.activeData?.list?.length > 0 && isEmpty) {
      setVoucherData(
        state.activeData.list.filter(item => item.type === 'offer')
      )
    }
  }, [state?.activeData?.list, isEmpty])

  useEffect(() => {
    setOpenSideSheet(Object.keys(state.voucherDetails).length > 0)
    /* istanbul ignore if  */
    if (!_isEmpty(state?.voucherDetails)) {
      track({
        [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.VOUCHER_DETAILS_VIEW_CLICKED,
        [GEV.AMPLITUDE_CTA_LOCATION]: {
          pageName: getPageTypeByUrl(),
          pageMeta: getCtaLocationMeta() || [],
        },
        [GEV.AMPLITUDE_VERTICAL]: 'rewards',
        [GEV.ASSET_TYPE]: 'REWARDS',
      })
    }
  }, [state?.voucherDetails])

  useEffect(() => {
    // Amplitude tracking
    if ((!state?.loading && !state?.error) || state?.activeData?.list) {
      trackVouchers(state?.activeData?.list)
    }
  }, [state?.activeData?.list, state?.error, state?.loading])

  const sideSheetContainer = () => {
    return (
      <SideSheet
        data-testid="voucher-sidesheet"
        open={openSideSheet}
        onClose={() => setOpenSideSheet(false)}
        direction="right"
        title="About this voucher"
        size={300}
        enableOverlay
        lockBackgroundScroll
        description=""
      >
        <SC.GlobalStyle openSideSheet={openSideSheet} />
        <VoucherDetailsPopUp
          dispatch={dispatch}
          voucherDetails={state.voucherDetails}
          pastTab={state.activeTab > 0}
          showQrCode={state.showQrCode}
          showHowToUse={state.showHowToUse}
          handleClose={dispatch.onHandleClose}
          imgLoading={state.imgLoading}
          isSideSheet={true}
        />
      </SideSheet>
    )
  }

  return voucherTreatment === 'B' && isMounted && voucherData?.length > 0 ? (
    <VoucherSwimLaneContainer data-testid="pdpVoucher">
      {isEmpty ? (
        ''
      ) : (
        <SC.PDPVoucherSwimlane
          vd={voucherData?.length}
          isThreeColumnLayout={isThreeColumnLayout}
        >
          <TitleBlock>
            <CouponSpace />
            <SwimlaneTitle>Apply at checkout</SwimlaneTitle>
          </TitleBlock>
          <DescBlock>
            <SwimlaneDesc>Stackable with promotions</SwimlaneDesc>
          </DescBlock>
          <SC.OGVoucherContainer>
            <SC.Grid container className="voucher-container">
              <VirtualCarousel
                items={voucherData}
                staticHeight={{
                  [SCREEN_SIZE.From.MobileL]: 110,
                  [SCREEN_SIZE.Below.MobileL]: 110,
                }}
                staticWidth={{
                  [SCREEN_SIZE.From.MobileL]: 252,
                  [SCREEN_SIZE.Below.MobileL]: 252,
                }}
              >
                {({ item, index }) => {
                  return (
                    <VoucherCard
                      i={index}
                      item={item}
                      error={activeDataList?.length < 1}
                      loading={state.loading}
                      dispatch={dispatch}
                      imgLoading={state.loading}
                      filterValue={state.activeFilter}
                      showFilter={state.showFilter}
                      track={track}
                      isVoucher
                    />
                  )
                }}
              </VirtualCarousel>
            </SC.Grid>
          </SC.OGVoucherContainer>
        </SC.PDPVoucherSwimlane>
      )}
      {sideSheetContainer()}
    </VoucherSwimLaneContainer>
  ) : null
}

const PdpVoucherSwimlane = props => (
  <EventTrackingConsumer>
    {({ track }) => (
      <AccountConsumer>
        {({ isLoggedIn }) =>
          isLoggedIn ? (
            <PdpVoucherSwimlaneContainer
              {...props}
              track={track}
              isLoggedIn={isLoggedIn}
            />
          ) : null
        }
      </AccountConsumer>
    )}
  </EventTrackingConsumer>
)

export default PdpVoucherSwimlane
